import { useState, useEffect, useMemo } from 'react';
import { X, Loader2, Layout, Monitor, ChevronDown } from 'lucide-react';
import * as AssessmentBuilderAPI from '../../services/assessmentBuilderApi';
import TemplatePreview from './ScreenBuilder/TemplatePreview';

// Normalize raw types (from CSV ITEM_TYPE, backend response_type, etc.) to canonical question types
const CANONICAL_TYPE_MAP = {
  // Ordinal
  multiple_choice: 'multiple_choice',
  ordinal: 'multiple_choice',
  ord: 'multiple_choice',
  likert: 'multiple_choice',
  // Rating / NRS
  rating: 'rating',
  nrs: 'rating',
  // Yes/No
  yes_no: 'yes_no',
  yesno: 'yes_no',
  'y/n': 'yes_no',
  yn: 'yes_no',
  boolean: 'yes_no',
  bool: 'yes_no',
  // Text input
  text: 'text',
  opentext: 'text',
  // Instructions (non-input, no template needed)
  instruction: 'instruction',
  inst: 'instruction',
  // Nominal
  nominal: 'nominal',
  // Numeric — stored type for NRS-style items; group with rating, not free text
  numeric: 'rating',
  num: 'rating',
  number: 'rating',
  // VAS
  vas: 'vas',
  // Other
  dropdown: 'dropdown',
  checkbox: 'checkbox',
  date: 'date',
};

const QUESTION_TYPE_DISPLAY = {
  multiple_choice: 'Ordinal',
  rating: 'Numeric Rating Scale',
  text: 'Free Text',
  yes_no: 'Yes / No',
  nominal: 'Nominal',
  vas: 'Visual Analog Scale',
  dropdown: 'Dropdown',
  checkbox: 'Checkbox',
  date: 'Date',
  instruction: 'Instructions',
};

const normalizeType = (raw) => {
  if (!raw) return 'text';
  const key = raw.toLowerCase().replace(/[-\/\s]/g, '_');
  return CANONICAL_TYPE_MAP[key] || CANONICAL_TYPE_MAP[raw] || 'text';
};

const displayType = (type) => QUESTION_TYPE_DISPLAY[type] || type;

// Map normalized frontend types to ALL backend question_type values to query templates for.
const BACKEND_TYPES_FOR_QUERY = {
  multiple_choice: ['multiple_choice', 'likert'],
  rating: ['rating'],
  vas: ['rating', 'vas'],
  yes_no: ['yes_no', 'boolean'],
  text: ['text'],
  nominal: ['nominal'],
  instruction: ['text'],
  dropdown: ['dropdown', 'nominal'],
  checkbox: ['checkbox'],
  date: ['date'],
};

// After fetching, filter templates to only show relevant ones for the canonical type.
// This prevents NRS showing VAS templates and vice versa (both are backend type 'rating').
const TEMPLATE_NAME_FILTERS = {
  rating: (t) => {
    const n = (t.name || '').toLowerCase();
    const p = (t.response_pattern || '').toLowerCase();
    // Show NRS templates + generic rating templates, exclude VAS-specific
    return p.includes('nrs') || n.includes('nrs') || n.includes('numeric rating')
      || (!n.includes('vas') && !n.includes('visual analog') && !p.includes('vas'));
  },
  vas: (t) => {
    const n = (t.name || '').toLowerCase();
    const p = (t.response_pattern || '').toLowerCase();
    // Show VAS templates + generic rating templates, exclude NRS-specific
    return p.includes('vas') || n.includes('vas') || n.includes('visual analog')
      || (!n.includes('nrs') && !n.includes('numeric rating') && !p.includes('nrs'));
  },
};

/**
 * ConfigureScreenLayoutModal
 *
 * Shown after the user clicks "+ Add N to Assessment" from the import summary.
 * Displays each detected question type with a count and a template picker.
 * On "Build Screens", calls the backend auto-build-screens endpoint, then
 * hands control back to ScreenBuilder.
 */
const ConfigureScreenLayoutModal = ({
  show,
  onClose,
  assessmentId,
  selectedItems,
  onBuildComplete,
  onSkip,
  deviceMode = 'ipad-10',
}) => {
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  const [error, setError] = useState(null);

  // { [questionType]: { templates: {...}, selectedTemplateId: null | number } }
  const [typeConfigs, setTypeConfigs] = useState({});

  // Group items by question_type → { type: string, count: number, sampleItem: obj }
  const typeGroups = useMemo(() => {
    if (!selectedItems?.length) return [];
    const groups = {};
    for (const item of selectedItems) {
      const raw = item.question_type || item.responseType || item.response_type || item.type || 'text';
      const qt = normalizeType(raw);
      if (!groups[qt]) {
        groups[qt] = { type: qt, count: 0, sampleItem: item };
      }
      groups[qt].count += 1;
    }
    return Object.values(groups).sort((a, b) => b.count - a.count);
  }, [selectedItems]);

  // Load templates for each question type on open
  useEffect(() => {
    if (!show || !assessmentId || typeGroups.length === 0) return;

    const loadAll = async () => {
      setLoading(true);
      setError(null);
      const configs = {};

      // Load templates for all types, querying all relevant backend types
      await Promise.all(
        typeGroups.map(async ({ type }) => {
          try {
            const backendTypes = BACKEND_TYPES_FOR_QUERY[type] || [type];
            // Fetch templates for all matching backend types and merge
            const allResults = await Promise.all(
              backendTypes.map((bt) =>
                AssessmentBuilderAPI.getAvailableTemplates(bt, assessmentId).catch(() => ({}))
              )
            );
            // Merge template lists, dedup by id
            const merged = { system_templates: [], sponsor_templates: [] };
            const seenIds = new Set();
            for (const result of allResults) {
              for (const key of ['system_templates', 'sponsor_templates']) {
                for (const t of result[key] || []) {
                  if (!seenIds.has(t.id)) {
                    seenIds.add(t.id);
                    merged[key].push(t);
                  }
                }
              }
            }
            configs[type] = {
              templates: merged,
              selectedTemplateId: null,
            };
          } catch (err) {
            console.warn(`Failed to load templates for ${type}:`, err);
            configs[type] = { templates: {}, selectedTemplateId: null };
          }
        })
      );

      setTypeConfigs(configs);
      setLoading(false);
    };

    loadAll();
  }, [show, assessmentId, typeGroups.length]);

  // Device tier classification for template sorting and grouping
  const allDeviceLabels = ['phone', 'mobile', 'tablet', 'ipad', 'desktop'];
  const activeLabels = useMemo(() => {
    const labels = [];
    const dk = (deviceMode || '').toLowerCase();
    if (dk.includes('phone') || dk.includes('iphone')) {
      labels.push('phone', 'mobile');
    } else if (dk.includes('ipad') || dk.includes('tablet')) {
      labels.push('tablet', 'ipad');
    } else if (dk.includes('desktop')) {
      labels.push('desktop');
    }
    return labels;
  }, [deviceMode]);

  const getDeviceTier = (name) => {
    const n = (name || '').toLowerCase();
    if (activeLabels.some((d) => n.includes(d))) return 0; // matches current device
    if (!allDeviceLabels.some((d) => n.includes(d))) return 1; // generic, no device keyword
    return 2; // mentions a different device
  };

  // Flatten all templates for a question type into a single list, with relevance filtering and sorting
  const getTemplateList = (type) => {
    const config = typeConfigs[type];
    if (!config?.templates) return [];
    const { system_templates = [], sponsor_templates = [] } = config.templates;
    let list = [
      ...system_templates.map((t) => ({ ...t, source: 'System' })),
      ...sponsor_templates.map((t) => ({ ...t, source: 'Sponsor' })),
    ];
    // Apply name/pattern filter if one exists for this type
    const filter = TEMPLATE_NAME_FILTERS[type];
    if (filter) {
      list = list.filter(filter);
    }
    // Sort: matching device > generic > non-matching device; within tier: Sponsor > System
    list.sort((a, b) => {
      const tierA = getDeviceTier(a.name);
      const tierB = getDeviceTier(b.name);
      if (tierA !== tierB) return tierA - tierB;
      if (a.source !== b.source) return a.source === 'Sponsor' ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return list;
  };

  const handleTemplateSelect = (questionType, templateId) => {
    setTypeConfigs((prev) => ({
      ...prev,
      [questionType]: {
        ...prev[questionType],
        selectedTemplateId: templateId,
      },
    }));
  };

  const getSelectedTemplate = (questionType) => {
    const config = typeConfigs[questionType];
    if (!config?.selectedTemplateId) return null;
    return getTemplateList(questionType).find(
      (t) => t.id === config.selectedTemplateId
    );
  };

  const handleBuildScreens = async () => {
    setBuilding(true);
    setError(null);

    try {
      // Build template_config from selections
      const template_config = {};
      for (const { type } of typeGroups) {
        const selectedId = typeConfigs[type]?.selectedTemplateId;
        template_config[type] = selectedId ? { template_id: selectedId } : null;
      }

      const item_ids = selectedItems.map((item) => item.id);

      const result = await AssessmentBuilderAPI.autoBuildScreens(assessmentId, {
        item_ids,
        template_config,
        device_mode: deviceMode,
      });

      onBuildComplete(selectedItems, result);
    } catch (err) {
      console.error('[ConfigureScreenLayout] Build failed:', err);
      setError(
        err.response?.data?.error ||
          err.message ||
          'Failed to build screens. Please try again.'
      );
      setBuilding(false);
    }
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-[60]">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl dark:shadow-gray-900/20 w-full max-w-2xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary-50 rounded-lg">
              <Layout className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Configure Screen Layout
              </h2>
              <p className="text-sm text-gray-500">
                {selectedItems?.length || 0} items &middot; {typeGroups.length}{' '}
                question type{typeGroups.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-primary-500 animate-spin mr-3" />
              <span className="text-gray-500">Loading templates...</span>
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-600 mb-2">
                Choose a layout template for each question type, or leave as
                &ldquo;Auto&rdquo; to use defaults. Screens will be created
                automatically with items grouped and positioned.
              </p>

              {typeGroups.map(({ type, count, sampleItem }) => {
                const templateList = getTemplateList(type);
                const selectedTemplate = getSelectedTemplate(type);

                return (
                  <div
                    key={type}
                    className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      {/* Type info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-gray-900">
                            {displayType(type)}
                          </span>
                          <span className="text-xs bg-gray-100 dark:bg-gray-800 text-gray-600 px-2 py-0.5 rounded-full">
                            {count} item{count !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <p className="text-xs text-gray-400 truncate">
                          e.g. &ldquo;{sampleItem?.text?.substring(0, 60) || 'Sample question'}...&rdquo;
                        </p>
                      </div>

                      {/* Template selector */}
                      <div className="flex-shrink-0 w-56">
                        <div className="relative">
                          <select
                            className="w-full appearance-none bg-white dark:bg-gray-800 border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                            value={
                              typeConfigs[type]?.selectedTemplateId || ''
                            }
                            onChange={(e) =>
                              handleTemplateSelect(
                                type,
                                e.target.value ? Number(e.target.value) : null
                              )
                            }
                          >
                            <option value="">Auto (default)</option>
                            {(() => {
                              const recommended = templateList.filter((t) => getDeviceTier(t.name) === 0);
                              const generic = templateList.filter((t) => getDeviceTier(t.name) === 1);
                              const other = templateList.filter((t) => getDeviceTier(t.name) === 2);
                              return (
                                <>
                                  {recommended.length > 0 && (
                                    <optgroup label="Recommended for this device">
                                      {recommended.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name} ({t.source})</option>
                                      ))}
                                    </optgroup>
                                  )}
                                  {generic.length > 0 && (
                                    <optgroup label="All templates">
                                      {generic.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name} ({t.source})</option>
                                      ))}
                                    </optgroup>
                                  )}
                                  {other.length > 0 && (
                                    <optgroup label="Other devices">
                                      {other.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name} ({t.source})</option>
                                      ))}
                                    </optgroup>
                                  )}
                                </>
                              );
                            })()}
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                        </div>
                      </div>
                    </div>

                    {/* Preview thumbnail when template selected */}
                    {selectedTemplate && (
                      <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3">
                        <TemplatePreview
                          template={selectedTemplate}
                          questionData={sampleItem}
                          maxWidth={480}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {/* Error display */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 rounded-b-xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            disabled={building}
          >
            Cancel
          </button>
          <div className="flex items-center gap-3">
            {onSkip && (
              <button
                onClick={() => onSkip(selectedItems)}
                disabled={building}
                className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white dark:bg-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Skip, Add to Sidebar Only
              </button>
            )}
            <button
              onClick={handleBuildScreens}
              disabled={loading || building}
              className="flex items-center gap-2 px-5 py-2.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {building ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Building Screens...
                </>
              ) : (
                <>
                  <Monitor className="w-4 h-4" />
                  Build Screens
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfigureScreenLayoutModal;
